/**
 * PictureMessageView.js
 * Talkroute
 *
 * Created by Vel Genov on 10/9/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
/* eslint react/prop-types: 0 */

import path from "path";
import mime from "mime-types";
import { saveAs } from "file-saver";
import React from "react";
import { connect } from "react-redux";
import {
  Modal,
  Button,
  Segment,
  Dimmer,
  Loader,
  Icon,
  Image,
} from "semantic-ui-react";
import { withToastManager } from "react-toast-notifications";
import pathParse from "path-parse";

import appConfig from "../../config/index";

import { TextMessageAttachment } from "../../services/dataClasses/TextMessageAttachment";
import { getMessageAttachment } from "../../actions/conversationActions";
import uiString from "../../constants/uiString";
import apiConstants from "../../constants/apiConstants";
import { IconButton } from "@material-ui/core";
import CloseIcon from "@resources/icons/cross.svg"
interface IPictureMessageViewProps {
  attachment: TextMessageAttachment;
  messageId: string;
  conversationId: string;
  filePath?: string;
  errorLoading?: boolean;
  toastManager: any;
  getMessageAttachment: (payload: {
    messageId: string;
    attachmentId: number;
    conversationId: string;
  }) => any;
}

/**
 * Display a single conversation message
 *
 */
class PictureMessageView extends React.Component<
  IPictureMessageViewProps,
  any
> {
  static defaultProps = {
    filePath: "",
  };

  showPictureView = () => {
    const { attachment, errorLoading } = this.props;
    if (errorLoading) {
      return this.showErrorLoading();
    }

    const { filePath, modalOpen, imageBase64 } = this.state;

    if (!filePath || !imageBase64) {
      return this.showLoader();
    }

    const popupTrigger = (
      <img
        className="message"
        alt="attachment"
        src={imageBase64}
        style={{ height: "150px", padding: "0px" }}
        onClick={this.handleOpen}
        onKeyPress={() => {}}
        role="presentation"
      />
    );

    return (
      <Modal
        trigger={popupTrigger}
        basic
        size="small"
        open={modalOpen}
        onClose={this.handleClose}
      >
        <IconButton
          style={{ position: "absolute", top: "-30px", right: "40px" }}
          onClick={this.handleClose}
        >
          <CloseIcon
            style={{
            fill: "var(--color-pumpkin-orange)",
            }}
          />
        </IconButton>
        <Modal.Content>
          <Image
            centered
            rounded
            src={imageBase64}
            style={{
              maxHeight: "70%",
              maxWidth: "60%",
            }}
          />
        </Modal.Content>
        <Modal.Actions
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Button
            style={{ display: "flex", justifyContent: "center" }}
            color="orange"
            inverted
            onClick={() => this.saveImageAs()}
          >
            {uiString.SAVE_AS}
          </Button>

          <Button
            style={{ display: "flex", justifyContent: "center" }}
            color="orange"
            inverted
            onClick={() => this.openImageIn()}
          >
            {uiString.OPEN_IMAGE}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  };

  /**
   * Show a picture message loader
   */
  showLoader = () => (
    <Segment
      className="message"
      style={{ width: "150px", height: "150px", padding: "0px", margin: "5px" }}
    >
      <Dimmer active>
        <Loader inverted />
      </Dimmer>
    </Segment>
  );

  /**
   * Show a loading error picture
   */
  showErrorLoading = () => (
    <Segment
      className="message"
      style={{ width: "150px", height: "150px", padding: "0px", margin: "5px" }}
    >
      <Dimmer active>
        <Icon name="warning circle" color="orange" size="huge" />
      </Dimmer>
    </Segment>
  );

  openImageIn = () => {
    const { filePath } = this.state;
    if (appConfig.electron) {
      appConfig.electron.shell.openPath(filePath).then();
    } else {
      const image = new global.Image();
      image.src = this.state.imageBase64;
      const w = window.open("", "_blank");
      if (w) {
        w.document.title = "Image View";
        w.document.write(image.outerHTML);
      }
    }
  };

  /**
   * Display a toast showind a problem saving a file
   */
  showProblemSavingFile = (message) => {
    const { toastManager } = this.props;

    if (message) {
      toastManager.add(<div>{message}</div>, {
        appearance: "error",
        autoDismiss: true,
        pauseOnHover: false,
      });
    }
  };

  /**
   * Opens the save as dialog
   */
  saveImageAs = async () => {
    const { filePath } = this.state;

    if (filePath) {
      try {
        const fileName = path.basename(filePath);
        const fileExtension = pathParse(fileName).ext;
        const updatedFileName =
          apiConstants().textMessageAttachmentSettings.ATTACHMENT_FILE_NAME +
          fileExtension;
        const fileData = appConfig.fileStorage!.get(filePath);
        const mimeType = mime.lookup(filePath);
        const file = new File([fileData], updatedFileName, { type: mimeType });

        saveAs(file);
      } catch (error) {
        const { message } = (error as any);
        this.showProblemSavingFile(message);
      }
    }
  };

  /**
   * Starts a request to fetch the image file
   */
  fetchImage = () => {
    const {
      getMessageAttachment: getMessageAttachmentLocal,
      messageId,
      attachment,
      conversationId,
    } = this.props;
    const { id: attachmentId } = attachment;

    getMessageAttachmentLocal({ messageId, attachmentId, conversationId });
  };

  /**
   * Handle opening of the picture message in a modal
   */
  handleOpen = () => this.setState({ modalOpen: true });

  /**
   * Handle closing of the picture message modal
   */
  handleClose = () => this.setState({ modalOpen: false });

  /**
   * Update the picture file path
   */
  updateFilePath = (filePath) => {
    this.setState({ filePath });
  };

  constructor(props) {
    super(props);
    const { filePath } = this.props;

    this.state = {
      filePath,
      modalOpen: false,
      imageBase64: "",
    };
  }

  componentDidMount() {
    const { conversationId, attachment } = this.props;
    const filePath = attachment.getFilePath(conversationId);

    this.updateFilePath(filePath);

    if (!filePath) {
      this.fetchImage();
    } else {
      let imageBase64 = "";
      const imageAsBlob = appConfig.fileStorage?.get(filePath);
      const imageReader = new FileReader();
      imageReader.onloadend = () => {
        imageBase64 = imageReader.result as string;
        imageBase64 = imageBase64.replace(
          "data:application/octet-stream;base64,",
          "data:image/png;base64, "
        );
        this.setState({
          imageBase64,
        });
      };
      imageReader.readAsDataURL(new Blob([(imageAsBlob as Buffer).buffer]));
    }
  }

  componentDidUpdate(prevProps) {
    const { filePath } = this.props;
    const { filePath: prevFilePath } = prevProps;

    if (filePath !== prevFilePath && filePath) {
      this.updateFilePath(filePath);
      let imageBase64 = "";
      if (filePath) {
        const imageAsBlob = appConfig.fileStorage?.get(filePath);
        const imageReader = new FileReader();
        imageReader.onloadend = () => {
          imageBase64 = imageReader.result as string;
          imageBase64 = imageBase64.replace(
            "data:application/octet-stream;base64,",
            "data:image/png;base64, "
          );
          this.setState({
            imageBase64,
          });
        };
        imageReader.readAsDataURL(new Blob([(imageAsBlob as Buffer).buffer]));
      }
    }
  }

  render = () => this.showPictureView();
}

/**
 * Map props to a redux state
 * @param {*} state
 */
const mapStateToProps = () => ({});

export default withToastManager(
  connect(mapStateToProps, { getMessageAttachment })(PictureMessageView)
);
